import type React from "react";
import { useEffect, useRef, useState } from "react";
import { apiGet, prettifyApiError } from "@/shared/api";

interface SyncStats {
  queued: number;
  running: number;
  succeeded_24h: number;
  failed_24h: number;
}

interface SyncStatusPillProps {
  /** Quando passa da false a true il pill polla aggressivamente per 5s aggiuntivi
   *  dopo il fine drain, cosi' la lista ordini si rinfresca all'ultima succession.
   */
  onDrainComplete?: () => void;
  /** Click handler opzionale: trasforma il pill in pulsante per aprire
   *  un pannello dettaglio con stato per modulo. Quando definito, il pill
   *  diventa un ``<button>`` con cursor-pointer e indicatore di toggle. */
  onClick?: () => void;
  /** Indica se il pannello dettaglio e' aperto: il pill mostra
   *  ``<>`` o ``><`` di conseguenza. */
  expanded?: boolean;
}

/**
 * Pill compatto sempre visibile sopra la lista: mostra in tempo reale
 * quanti job sync sono in coda + running. Polling adattivo:
 * - 6s a riposo (running=0, queued=0)
 * - 1.5s quando c'e' lavoro attivo
 *
 * Il backend packaged a volte ha il worker stuck (vedi audit 2026-04-29):
 * questo pill rende immediatamente visibile la situazione invece di lasciare
 * Davide a fissare un'area bianca senza capire se l'app sta lavorando.
 */
export function SyncStatusPill({
  onDrainComplete,
  onClick,
  expanded = false,
}: SyncStatusPillProps) {
  const [stats, setStats] = useState<SyncStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Ref-based callback: tenere `onDrainComplete` in dep-array faceva
  // restart del polling ad ogni render del parent (audit HIGH-1
  // 2026-04-29) perche' OrdiniPage ricrea la callback ogni volta che
  // arriva nuovo data dal backend → loop di restart che vanifica il
  // tracking di `prevActive` e blocca il trigger drain-complete.
  const onDrainCompleteRef = useRef(onDrainComplete);
  useEffect(() => {
    onDrainCompleteRef.current = onDrainComplete;
  }, [onDrainComplete]);

  useEffect(() => {
    let cancelled = false;
    let timerId: number | null = null;
    let prevActive = false;
    // Audit 2026-05-11 L18: backoff progressivo durante sync long-running.
    // Prima: 1.5s costanti durante drain. Un sync da 5 min = ~200 GET
    // ``/api/sync/stats`` (rumore log backend). Ora: dopo i primi 6
    // sample attivi (~9s) rallentiamo a 3s.
    let activeSamples = 0;

    const tick = async () => {
      try {
        const data = await apiGet<SyncStats>("/api/sync/stats");
        if (cancelled) return;
        setStats(data);
        setError(null);
        const active = data.queued > 0 || data.running > 0;
        if (prevActive && !active) {
          onDrainCompleteRef.current?.();
        }
        prevActive = active;
        if (active) {
          activeSamples += 1;
        } else {
          activeSamples = 0;
        }
        // Cadence: 1.5s primi 6 sample attivi, poi 3s. A riposo 6s.
        const delay = !active ? 6000 : activeSamples <= 6 ? 1500 : 3000;
        timerId = window.setTimeout(() => void tick(), delay);
      } catch (err) {
        if (cancelled) return;
        // prettifyApiError, come nel resto del frontend: mappa già «failed to
        // fetch» in «Backend irraggiungibile», e un 403/429/500 nel SUO
        // messaggio. Prima il messaggio vero era calcolato qui e buttato via,
        // e l'etichetta fissa mandava a controllare il container mentre il
        // backend rispondeva benissimo.
        setError(prettifyApiError(err));
        timerId = window.setTimeout(() => void tick(), 4000);
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, []);

  // Wrapper che produce <button> o <div> in base a onClick. Mantiene
  // gli stessi stili inline del pill originale per non rompere il layout.
  const wrap = (className: string, body: React.ReactNode) => {
    const baseCls = `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] ${className}`;
    if (onClick) {
      return (
        <button
          type="button"
          onClick={onClick}
          aria-expanded={expanded}
          className={`${baseCls} cursor-pointer transition-colors hover:brightness-95`}
        >
          {body}
          <span className="text-ink-3/70 text-[10px] ml-0.5">{expanded ? "▴" : "▾"}</span>
        </button>
      );
    }
    return <div className={baseCls}>{body}</div>;
  };

  if (error) {
    return wrap(
      "bg-red-soft text-ink border border-red/20",
      <>
        <span className="w-1.5 h-1.5 rounded-full bg-red" />
        {/* max-w + truncate: il pill sta in una barra stretta, ma il messaggio
            intero resta leggibile nel tooltip invece di sparire. */}
        <span className="max-w-[260px] truncate" title={error}>
          {error}
        </span>
      </>,
    );
  }

  if (!stats) {
    return wrap(
      "bg-paper-2 text-ink-3 border border-line",
      <>
        <span className="w-1.5 h-1.5 rounded-full bg-ink-3 animate-pulse" />
        Connessione…
      </>,
    );
  }

  const active = stats.queued > 0 || stats.running > 0;

  if (!active) {
    return wrap(
      "bg-green-soft text-ink border border-green/20",
      <>
        <span className="w-1.5 h-1.5 rounded-full bg-green" />
        Tutto sincronizzato
        {stats.succeeded_24h > 0 && (
          <span className="text-ink-3 font-mono tabular-nums">· {stats.succeeded_24h} ok 24h</span>
        )}
        {stats.failed_24h > 0 && (
          <span className="text-red font-mono tabular-nums">· {stats.failed_24h} errori</span>
        )}
      </>,
    );
  }

  return wrap(
    "bg-violet-soft text-ink border border-violet/20",
    <>
      <span className="relative flex w-2 h-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-violet opacity-60 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-violet" />
      </span>
      <span>Sync in corso</span>
      <span className="text-ink-3 font-mono tabular-nums">
        {stats.running > 0 && `· ${stats.running} attiv${stats.running > 1 ? "i" : "o"}`}
        {stats.queued > 0 && ` · ${stats.queued} in coda`}
      </span>
    </>,
  );
}
